import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import {
	parseAsArrayOf,
	parseAsBoolean,
	parseAsInteger,
	parseAsString,
	parseAsStringEnum,
	parseAsStringLiteral,
	useQueryState,
	useQueryStates,
} from 'nuqs';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router';
import { Waypoint } from 'react-waypoint';
import { useDebounceCallback } from '../../../../hooks/useDebounceValue';
import { MastheadPortalId } from '../../../../models/AdminModels';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { getDisplayName } from '../../../../models/NameUtils';
import { useUserQuery } from '../../../../queries';
import { useAdminAccountExportMutation } from '../../../../queries/Admin/Account/useAdminAccountExportMutation';
import { useAdminAccountFilterQuery } from '../../../../queries/Admin/Account/useAdminAccountFilterQuery';
import * as AdminApi from '../../../../viewmodels/AdminViewModels';
import {
	Dropdown,
	DropdownCheckboxItem,
	DropdownContent,
	DropdownRadioGroup,
	DropdownRadioItem,
	DropdownSelectTrigger,
} from '../../../../web/components-v2/Dropdown';
import { Input, InputWrapper } from '../../../../web/components-v2/Input';
import {
	Select,
	SelectCheckboxItem,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '../../../../web/components-v2/Select';
import { Breadcrumb, BreadcrumbList, BreadcrumbPage } from '../../../../web/components/Breadcrumb';
import { ExportConfirmationModalV2 } from '../../../../web/components/ExportConfirmation';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { Portal } from '../../../../web/components/Portal';
import {
	IRadioButtonDialogOption,
	RadioButtonOptionsDialog,
} from '../../../../web/components/RadioButtonOptionsDialog';
import { ExportIcon } from '../../../../web/components/svgs/icons/ExportIcon';
import { SearchIcon2 } from '../../../../web/components/svgs/icons/SearchIcon2';
import { UserSelectModal } from '../../../../web/components/UserSelectModal';
import { brandPrimaryText } from '../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { AccountRow } from '../../../components/AccountRow';
import { AccountsStatsHeader } from '../../../components/AccountsStatsHeader';
import { isResellerSession } from '../../../contexts/ResellerContext';
import { appendBillingCycleCriteria, BILLING_CYCLE_OPTIONS, BillingCycle } from '../../reports/ReportsMonthlyRenewal';
import { styleSheet } from './styles';

const SESSION_STORAGE_KEY_LATEST_ADMIN_ACCOUNTS_URL = `admin-latest-accounts-url`;
export const getSessionAdminAccountsUrl = () => {
	return (sessionStorage.getItem(SESSION_STORAGE_KEY_LATEST_ADMIN_ACCOUNTS_URL) ?? Api.isProdEnvironment)
		? `/accounts?planId=business-paid`
		: `/accounts`;
};

enum PlanFilterOptions {
	All = 'all',
	Business = 'business',
	BusinessPaid = 'business-paid',
	Personal = 'personal',
}

const planOptions = [
	{ label: 'All Accounts', value: PlanFilterOptions.All },
	{ label: 'Business', value: PlanFilterOptions.Business },
	{ label: 'Business Paid', value: PlanFilterOptions.BusinessPaid },
	{ label: 'Personal', value: PlanFilterOptions.Personal },
];

const industryOptions: Api.Industry[] = [
	Api.Industry.Insurance,
	Api.Industry.RealEstate,
	Api.Industry.Financial,
	Api.Industry.Mortgage,
	Api.Industry.Legal,
	Api.Industry.Accounting,
	Api.Industry.NonProfit,
	Api.Industry.HomeMaintenance,
	Api.Industry.Health,
	Api.Industry.Dental,
	Api.Industry.Chiropractic,
];

const industryRadios: IRadioButtonDialogOption<Api.Industry>[] = industryOptions.map(x => {
	return {
		key: x,
		representedObject: x,
		title: x,
	};
});

export function Accounts() {
	const errorMessages = useErrorMessages();
	const history = useHistory();
	const userSession = useUserSession();
	const isReseller = isResellerSession(userSession);
	const location = useLocation();
	const { logApiError } = useEventLogging('Accounts');
	const [accountFilter, setAccountFilter] = useQueryStates({
		successManagerId: parseAsString,
		salesRepId: parseAsString,
		industry: parseAsStringEnum(Object.values(Api.Industry)),
	});
	const [searchQuery, setSearchQuery] = useQueryState('searchQuery', parseAsString.withDefault(''));
	const [flagged, setFlagged] = useQueryState('flagged', parseAsBoolean.withDefault(false));
	const [grey, setGrey] = useQueryState('grey', parseAsBoolean.withDefault(false));
	const [green, setGreen] = useQueryState('green', parseAsBoolean.withDefault(false));
	const [yellow, setYellow] = useQueryState('yellow', parseAsBoolean.withDefault(false));
	const [orange, setOrange] = useQueryState('orange', parseAsBoolean.withDefault(false));
	const [red, setRed] = useQueryState('red', parseAsBoolean.withDefault(false));
	const [planIdParam, setPlanId] = useQueryState(
		'planId',
		parseAsStringEnum(Object.values(PlanFilterOptions)).withDefault(PlanFilterOptions.All)
	);
	// Resellers always see all accounts; the plan filter is hidden for them
	const planId = isReseller ? PlanFilterOptions.All : planIdParam;
	// Clear the URL param for resellers to keep URL state clean
	if (isReseller && planIdParam !== PlanFilterOptions.All) {
		setPlanId(PlanFilterOptions.All);
	}
	const [billing, setBilling] = useQueryState(
		'billing',
		parseAsArrayOf(parseAsInteger).withDefault([BillingCycle.All])
	);
	const [sortBy, setSortBy] = useQueryState('sortBy', parseAsString.withDefault('creationDate'));
	const [sort, setSort] = useQueryState('sort', parseAsStringLiteral(['asc', 'desc']).withDefault('desc'));
	const [hasNoSalesNotes, setHasNoSalesNotes] = useQueryState('hasNoSalesNotes', parseAsBoolean.withDefault(false));
	const [hasAds, setHasAds] = useQueryState('hasAds', parseAsBoolean.withDefault(false));
	const [hasFacebookAds, setHasFacebookAds] = useQueryState('hasFacebookAds', parseAsBoolean.withDefault(false));
	const [hasGoogleLsa, setHasGoogleLsa] = useQueryState('hasGoogleLsa', parseAsBoolean.withDefault(false));
	const [hasGooglePpc, setHasGooglePpc] = useQueryState('hasGooglePpc', parseAsBoolean.withDefault(false));
	const [hasWebsites, setHasWebsites] = useQueryState('hasWebsites', parseAsBoolean.withDefault(false));
	const [customCalendarEnabled, setCustomCalendarEnabled] = useQueryState(
		'customCalendarEnabled',
		parseAsBoolean.withDefault(false)
	);
	const [customCalendarIntakeFormNeeded, setCustomCalendarIntakeFormNeeded] = useQueryState(
		'customCalendarIntakeFormNeeded',
		parseAsBoolean.withDefault(false)
	);

	const [searchValue, setSearchValue] = React.useState(searchQuery);
	const debouncedSetSearchQuery = useDebounceCallback(setSearchQuery);
	const handleSearchInputChange = (value: string) => {
		setSearchValue(value);
		debouncedSetSearchQuery(value);
	};
	const handleClearFilters = () => {
		history.replace(`/accounts`);
	};
	React.useEffect(() => {
		if (searchQuery !== searchValue) {
			setSearchValue(searchQuery);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchQuery]);
	const [showingOwnedByModal, setShowingOwnedByModal] = React.useState(false);
	const [showingSoldByModal, setShowingSoldByModal] = React.useState(false);
	const [showingIndustryModal, setShowingIndustryModal] = React.useState(false);
	const adminAccountExportMutation = useAdminAccountExportMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('Accounts', error);
		},
	});
	const advancedFiltersState = React.useMemo(
		() => ({
			hasNoSalesNotes,
			hasAds,
			hasFacebookAds,
			hasGoogleLsa,
			hasGooglePpc,
			customCalendarEnabled,
			customCalendarIntakeFormNeeded,
			hasWebsites,
		}),
		[
			hasNoSalesNotes,
			hasAds,
			hasFacebookAds,
			hasGoogleLsa,
			hasGooglePpc,
			customCalendarEnabled,
			customCalendarIntakeFormNeeded,
			hasWebsites,
		]
	);

	const accountFilterRequest = React.useMemo(() => {
		const criteria: AdminApi.IAccountFilterCriteria[] = []; // and'ed
		const colorFilterCriteria: AdminApi.IAccountFilterCriteria[] = []; // or'ed
		if (searchQuery.length > 0) {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.Name,
				value: searchQuery,
			});
		}
		if (planId === 'business' || planId === 'personal') {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.AccountType,
				value: planId,
			});
		}
		if (planId === PlanFilterOptions.BusinessPaid) {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.AccountType,
				value: 'business',
			});
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.BillingType,
				value: Api.BillingType.Paid,
			});
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.CancellationDate,
				value: 'false',
			});
		}
		if (accountFilter.successManagerId) {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.SuccessManager,
				value: accountFilter.successManagerId,
			});
		}
		if (accountFilter.salesRepId) {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.SalesRep,
				value: accountFilter.salesRepId,
			});
		}
		if (accountFilter.industry) {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.Industry,
				value: accountFilter.industry,
			});
		}
		if (green) {
			colorFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.ActiveUseStatus,
				value: 'green',
			});
		}
		if (grey) {
			colorFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.ActiveUseStatus,
				value: 'gray',
			});
		}
		if (yellow) {
			colorFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.ActiveUseStatus,
				value: 'yellow',
			});
		}
		if (orange) {
			colorFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.ActiveUseStatus,
				value: 'orange',
			});
		}
		if (red) {
			colorFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.ActiveUseStatus,
				value: 'red',
			});
		}
		if (flagged) {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.Flagged,
				value: 'true',
			});
		}
		if (colorFilterCriteria?.length > 0) {
			criteria.push({
				criteria: colorFilterCriteria,
				op: Api.FilterOperator.Or,
			});
		}
		appendBillingCycleCriteria({ billingCycleFilter: billing, criteria });

		// Advanced filters (OR logic)
		const advancedFilterCriteria: AdminApi.IAccountFilterCriteria[] = [];
		if (advancedFiltersState.hasNoSalesNotes) {
			advancedFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.HasNoSalesNotes,
				value: 'true',
			});
		}
		if (advancedFiltersState.hasAds) {
			advancedFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.HasAds,
				value: 'true',
			});
		}
		if (advancedFiltersState.hasFacebookAds) {
			advancedFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.HasFacebookAds,
				value: 'true',
			});
		}
		if (advancedFiltersState.hasGoogleLsa) {
			advancedFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.HasGoogleLocalServiceAds,
				value: 'true',
			});
		}
		if (advancedFiltersState.hasGooglePpc) {
			advancedFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.HasGooglePpc,
				value: 'true',
			});
		}
		if (advancedFiltersState.customCalendarEnabled) {
			advancedFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.CustomCalendarEnabled,
				value: 'true',
			});
		}
		if (advancedFiltersState.customCalendarIntakeFormNeeded) {
			advancedFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.CustomCalendarIntakeFormNeeded,
				value: 'true',
			});
		}
		if (advancedFiltersState.hasWebsites) {
			advancedFilterCriteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.Websites,
				value: 'true',
			});
		}
		if (advancedFilterCriteria?.length > 0) {
			criteria.push({
				criteria: advancedFilterCriteria,
				op: Api.FilterOperator.Or,
			});
		}

		const finalRequest = {
			criteria: [...criteria],
			op: Api.FilterOperator.And,
		} as AdminApi.IAccountsFilterRequest;

		return finalRequest;
	}, [planId, searchQuery, billing, accountFilter, advancedFiltersState, flagged, green, orange, red, yellow, grey]);
	const adminAccountFilterQuery = useAdminAccountFilterQuery({
		filter: accountFilterRequest,
		sort,
		sortBy,
		autocompleteFragment: searchQuery ? searchQuery : undefined,
	});
	const soldByUserQuery = useUserQuery({
		id: accountFilter.salesRepId,
		enabled: Boolean(accountFilter.salesRepId),
	});
	const ownedByUserQuery = useUserQuery({
		id: accountFilter.successManagerId,
		enabled: Boolean(accountFilter.successManagerId),
	});
	React.useEffect(() => {
		document.title = 'Admin';
	}, [adminAccountFilterQuery.data]);
	React.useEffect(() => {
		if (adminAccountFilterQuery.error) {
			errorMessages?.pushApiError('Could not load the account information');
		}
	}, [adminAccountFilterQuery.error, errorMessages]);
	React.useEffect(() => {
		sessionStorage.setItem(SESSION_STORAGE_KEY_LATEST_ADMIN_ACCOUNTS_URL, location.pathname + location.search);
	}, [location.pathname, location.search]);

	let accountDropdownSelectedText = 'All Accounts';
	if (accountFilter.successManagerId) {
		accountDropdownSelectedText =
			accountFilter.successManagerId === userSession.user.id
				? 'Accounts I Own'
				: `Accounts Owned By ${ownedByUserQuery.data ? getDisplayName(ownedByUserQuery.data) : ''}`;
	} else if (accountFilter.salesRepId) {
		accountDropdownSelectedText =
			accountFilter.salesRepId === userSession.user.id
				? 'Accounts I Sold'
				: `Accounts Sold By ${soldByUserQuery.data ? getDisplayName(soldByUserQuery.data) : ''}`;
	} else if (accountFilter.industry) {
		accountDropdownSelectedText = `Accounts by industry: ${accountFilter.industry}`;
	}
	const isOwnedByMode = Boolean(accountFilter.successManagerId);
	const planFilterChange = (option: PlanFilterOptions) => {
		if (option !== planId) {
			setPlanId(option);
		}
	};
	const newAccountClick = () => {
		history.push('/accounts/create-account');
	};

	const getAdvancedFilterDisplayText = () => {
		if (
			!hasNoSalesNotes &&
			!hasAds &&
			!hasFacebookAds &&
			!hasGoogleLsa &&
			!hasGooglePpc &&
			!customCalendarEnabled &&
			!customCalendarIntakeFormNeeded &&
			!hasWebsites
		) {
			return 'None selected';
		}

		const selectedFilters = [
			hasNoSalesNotes ? 'No Sales Notes' : '',
			customCalendarEnabled ? 'Custom Content' : '',
			customCalendarIntakeFormNeeded ? 'Content Intake Needed' : '',
			hasWebsites ? 'Websites' : '',
			hasAds ? 'Ads (any type)' : '',
			hasFacebookAds ? 'Meta Ads' : '',
			hasGoogleLsa ? 'Google LSA' : '',
			hasGooglePpc ? 'Google PPC' : '',
		].filter(Boolean);

		return selectedFilters.join(', ');
	};
	const onExportAccountsButtonClicked = () => {
		adminAccountExportMutation.mutate({
			request: {
				filter: accountFilterRequest,
				useSnapshots: false,
			},
		});
	};
	const sortByDate = (sortDescriptor: Api.ISortDescriptor) => () => {
		if (sort !== sortDescriptor.sort) {
			setSort(sortDescriptor.sort);
		}
		if (sortBy !== sortDescriptor.sortBy) {
			setSortBy(sortDescriptor.sortBy);
		}
	};
	const onOwnedByModalRequestClose = (user?: Api.UserViewModel, cancel?: boolean) => {
		if (!cancel) {
			setAccountFilter({ successManagerId: user.id, salesRepId: null, industry: null });
		}
		setShowingOwnedByModal(false);
	};
	const onSoldByModalRequestClose = (user?: Api.UserViewModel, cancel?: boolean) => {
		if (!cancel) {
			setAccountFilter({ salesRepId: user.id, industry: null, successManagerId: null });
		}
		setShowingSoldByModal(false);
	};
	const onRemoveFromTagDialogRequestClose = (result?: IRadioButtonDialogOption<Api.Industry>, canceled?: boolean) => {
		if (!canceled) {
			setAccountFilter({ industry: result.representedObject, salesRepId: null, successManagerId: null });
		}
		setShowingIndustryModal(false);
	};

	let accountsTitle = 'All Accounts';

	if (accountFilter.successManagerId === userSession.user.id) {
		accountsTitle = userSession.user.firstName + ' ' + userSession.user.lastName;
	} else if (accountFilter.successManagerId && ownedByUserQuery.data) {
		accountsTitle = getDisplayName(ownedByUserQuery.data);
	} else if (accountFilter.salesRepId && soldByUserQuery.data) {
		accountsTitle = getDisplayName(soldByUserQuery.data);
	} else if (accountFilter.industry) {
		accountsTitle = `${accountFilter.industry} Accounts`;
	} else if (planId === PlanFilterOptions.BusinessPaid) {
		accountsTitle = 'All Paid Accounts';
	}
	const isFiltered =
		flagged ||
		green ||
		grey ||
		yellow ||
		orange ||
		red ||
		isOwnedByMode ||
		planId !== PlanFilterOptions.BusinessPaid ||
		!!searchQuery ||
		!!sort ||
		!!sortBy ||
		(!accountFilter.successManagerId && !accountFilter.salesRepId && !accountFilter.industry) ||
		hasNoSalesNotes ||
		hasAds ||
		hasFacebookAds ||
		hasGoogleLsa ||
		hasGooglePpc ||
		customCalendarEnabled ||
		customCalendarIntakeFormNeeded ||
		hasWebsites;

	const renderCreatedHeader = () => {
		if (sort === 'asc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderStandard)}
					onClick={sortByDate({ sort: 'desc', sortBy: 'creationDate' })}
					style={{
						color: sortBy === 'creationDate' ? 'black' : '',
					}}
				>
					Created &#x25B2;
				</div>
			);
		} else if (sort === 'desc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderStandard)}
					onClick={sortByDate({ sort: 'asc', sortBy: 'creationDate' })}
					style={{
						color: sortBy === 'creationDate' ? 'black' : '',
					}}
				>
					Created &#x25BC;
				</div>
			);
		}
	};
	const renderRenewalHeader = () => {
		if (sort === 'asc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderStandard)}
					onClick={sortByDate({
						sort: 'desc',
						sortBy: 'planDetails.renewalDate',
					})}
					style={{
						color: sortBy === 'planDetails.renewalDate' ? 'black' : '',
					}}
				>
					Renewal &#x25B2;
				</div>
			);
		} else if (sort === 'desc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderStandard)}
					onClick={sortByDate({
						sort: 'asc',
						sortBy: 'planDetails.renewalDate',
					})}
					style={{
						color: sortBy === 'planDetails.renewalDate' ? 'black' : '',
					}}
				>
					Renewal &#x25BC;
				</div>
			);
		}
	};
	const renderLastMeeting = () => {
		if (sort === 'asc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderStandard)}
					onClick={sortByDate({
						sort: 'desc',
						sortBy: 'additionalInfo.lastCsmMeeting',
					})}
					style={{
						color: sortBy === 'additionalInfo.lastCsmMeeting' ? 'black' : '',
					}}
				>
					Last Meeting &#x25B2;
				</div>
			);
		} else if (sort === 'desc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderStandard)}
					onClick={sortByDate({
						sort: 'asc',
						sortBy: 'additionalInfo.lastCsmMeeting',
					})}
					style={{
						color: sortBy === 'additionalInfo.lastCsmMeeting' ? 'black' : '',
					}}
				>
					Last Meeting &#x25BC;
				</div>
			);
		}
	};
	const renderNextMeeting = () => {
		if (sort === 'asc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderLast)}
					onClick={sortByDate({
						sort: 'desc',
						sortBy: 'additionalInfo.nextMeeting',
					})}
					style={{
						color: sortBy === 'additionalInfo.nextMeeting' ? 'black' : '',
					}}
				>
					Next Meeting &#x25B2;
				</div>
			);
		} else if (sort === 'desc') {
			return (
				<div
					className={css(styleSheet.accountListHeaderLast)}
					onClick={sortByDate({
						sort: 'asc',
						sortBy: 'additionalInfo.nextMeeting',
					})}
					style={{
						color: sortBy === 'additionalInfo.nextMeeting' ? 'black' : '',
					}}
				>
					Next Meeting &#x25BC;
				</div>
			);
		}
	};
	return (
		<div className={css(styleSheet.root)} data-scroll-restoration-id='admin-accounts-page'>
			<Portal destination={MastheadPortalId}>
				<Breadcrumb>
					<BreadcrumbList>
						<BreadcrumbPage>Accounts</BreadcrumbPage>
					</BreadcrumbList>
				</Breadcrumb>
			</Portal>
			<div className={css(styleSheet.contentContainer)}>
				<div className={css(styleSheet.actionBarContainer)}>
					<div className={css(styleSheet.actionBarFilters)}>
						<InputWrapper styles={[styleSheet.triggerContainer]}>
							<SearchIcon2 width={16} height={16} />
							<Input
								onChange={ev => handleSearchInputChange(ev.target.value)}
								placeholder='Search Company'
								value={searchValue || ''}
							/>
						</InputWrapper>
						{!isReseller && (
							<span className={css(styleSheet.dropdownContainerWithMargin)}>
								<Select items={planOptions} value={planId} onValueChange={planFilterChange}>
									<SelectTrigger styles={[styleSheet.typeSelectTrigger]}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value={PlanFilterOptions.All}>All Accounts</SelectItem>
										<SelectItem value={PlanFilterOptions.Business}>Business</SelectItem>
										<SelectItem value={PlanFilterOptions.BusinessPaid}>Business Paid</SelectItem>
										<SelectItem value={PlanFilterOptions.Personal}>Personal</SelectItem>
									</SelectContent>
								</Select>
							</span>
						)}
						<span className={css(styleSheet.dropdownContainerWithMargin)}>
							<Dropdown>
								<DropdownSelectTrigger styles={[styleSheet.selectTrigger]}>
									{accountDropdownSelectedText}
								</DropdownSelectTrigger>
								<DropdownContent>
									<DropdownRadioGroup
										value={
											accountFilter.successManagerId
												? accountFilter.successManagerId === userSession.user.id
													? 'ownedByMe'
													: 'ownedBy'
												: accountFilter.salesRepId
													? accountFilter.salesRepId === userSession.user.id
														? 'soldByMe'
														: 'soldBy'
													: accountFilter.industry
														? 'industry'
														: 'all'
										}
									>
										<DropdownRadioItem
											value='all'
											onClick={() => setAccountFilter({ successManagerId: null, salesRepId: null, industry: null })}
										>
											All accounts
										</DropdownRadioItem>
										<DropdownRadioItem
											value='ownedByMe'
											onClick={() =>
												setAccountFilter({ successManagerId: userSession.user.id, salesRepId: null, industry: null })
											}
										>
											Accounts I own
										</DropdownRadioItem>
										<DropdownRadioItem value='ownedBy' onClick={() => setShowingOwnedByModal(true)} closeOnClick>
											Accounts owned by...
										</DropdownRadioItem>
										<DropdownRadioItem
											value={'soldByMe'}
											onClick={() =>
												setAccountFilter({ salesRepId: userSession.user.id, successManagerId: null, industry: null })
											}
										>
											Accounts I sold
										</DropdownRadioItem>
										<DropdownRadioItem value='soldBy' onClick={() => setShowingSoldByModal(true)} closeOnClick>
											Accounts sold by...
										</DropdownRadioItem>
										<DropdownRadioItem value='industry' onClick={() => setShowingIndustryModal(true)} closeOnClick>
											Accounts by industry...
										</DropdownRadioItem>
									</DropdownRadioGroup>
								</DropdownContent>
							</Dropdown>
						</span>
						{!isReseller && (
							<span className={css(styleSheet.dropdownContainerWithMargin)}>
								<Select
									multiple
									items={BILLING_CYCLE_OPTIONS.map(o => ({
										label: o.text,
										value: o.dataContext,
									}))}
									value={billing}
									onValueChange={value => setBilling(value)}
								>
									<SelectTrigger>
										<SelectValue>
											{billing.includes(BillingCycle.All)
												? 'All Billing Cycles'
												: `Renewal period: ${billing.sort((a, b) => a - b).join(', ')} months`}
										</SelectValue>
									</SelectTrigger>
									<SelectContent>
										{BILLING_CYCLE_OPTIONS.map(o => {
											return (
												<SelectCheckboxItem key={o.dataContext} value={o.dataContext}>
													{o.text}
												</SelectCheckboxItem>
											);
										})}
									</SelectContent>
								</Select>
							</span>
						)}
						{!isReseller && (
							<span className={css(styleSheet.dropdownContainer)}>
								<span className={css(styleSheet.searchBarText)}>Advanced: </span>
								<Dropdown>
									<DropdownSelectTrigger styles={[styleSheet.selectTrigger]}>
										{getAdvancedFilterDisplayText()}
									</DropdownSelectTrigger>
									<DropdownContent>
										<DropdownCheckboxItem
											checked={hasNoSalesNotes}
											onCheckedChange={value => setHasNoSalesNotes(value)}
										>
											No Sales Notes
										</DropdownCheckboxItem>
										<DropdownCheckboxItem
											checked={customCalendarEnabled}
											onCheckedChange={value => setCustomCalendarEnabled(value)}
										>
											Custom Content
										</DropdownCheckboxItem>
										<DropdownCheckboxItem
											checked={customCalendarIntakeFormNeeded}
											onCheckedChange={value => setCustomCalendarIntakeFormNeeded(value)}
										>
											Content Intake Needed
										</DropdownCheckboxItem>
										<DropdownCheckboxItem checked={hasWebsites} onCheckedChange={value => setHasWebsites(value)}>
											Websites
										</DropdownCheckboxItem>
										<DropdownCheckboxItem checked={hasAds} onCheckedChange={value => setHasAds(value)}>
											Ads (any type)
										</DropdownCheckboxItem>
										<DropdownCheckboxItem checked={hasFacebookAds} onCheckedChange={value => setHasFacebookAds(value)}>
											Meta / Facebook Ads
										</DropdownCheckboxItem>
										<DropdownCheckboxItem checked={hasGoogleLsa} onCheckedChange={value => setHasGoogleLsa(value)}>
											Google LSA
										</DropdownCheckboxItem>
										<DropdownCheckboxItem checked={hasGooglePpc} onCheckedChange={value => setHasGooglePpc(value)}>
											Google PPC
										</DropdownCheckboxItem>
									</DropdownContent>
								</Dropdown>
							</span>
						)}
					</div>
					<div className={css(baseStyleSheet.horizontalStack)}>
						<button className={css(baseStyleSheet.ctaButton, styleSheet.ctaButton)} onClick={newAccountClick}>
							<span>New Account</span>
						</button>
					</div>
				</div>
				<div className={css(styleSheet.accountsTitleHeader)}>
					{!isFiltered ? (
						<div className={css(styleSheet.accountsTitle)}>{accountsTitle}</div>
					) : (
						<div className={css(styleSheet.accountsTitle)}>
							{accountsTitle}
							<button className={css(styleSheet.clearFilters)} onClick={handleClearFilters}>
								Clear filters
							</button>
						</div>
					)}
					<button className={css(styleSheet.exportButton)} onClick={onExportAccountsButtonClicked}>
						<ExportIcon fillColor={brandPrimaryText} />
						<span style={{ marginLeft: 6 }}>Export to CSV</span>
					</button>
				</div>
				<ExportConfirmationModalV2
					mutationResult={adminAccountExportMutation}
					modalProps={{
						isOpen: !adminAccountExportMutation.isIdle,
						onRequestClose: () => adminAccountExportMutation.reset(),
					}}
				/>
				<AccountsStatsHeader
					filterCriteria={accountFilterRequest}
					flaggedCheck={flagged}
					greenCheck={green}
					greyCheck={grey}
					onChangedFlaggedCheck={() => setFlagged(!flagged)}
					onChangedGreenCheck={() => setGreen(!green)}
					onChangedGreyCheck={() => setGrey(!grey)}
					onChangedOrangeCheck={() => setOrange(!orange)}
					onChangedRedCheck={() => setRed(!red)}
					onChangedYellowCheck={() => setYellow(!yellow)}
					orangeCheck={orange}
					redCheck={red}
					yellowCheck={yellow}
					totalNumberOfResults={adminAccountFilterQuery.data?.pages?.[0]?.totalCount}
				/>
				<div className={css(styleSheet.accountListHeader)}>
					<div className={css(styleSheet.accountListHeaderCompanyName)}>Company Name</div>
					{isOwnedByMode ? (
						<>
							<div className={css(styleSheet.accountListHeaderStandard)}>Industry</div>
							<div className={css(styleSheet.accountListHeaderStandard)}>Stage</div>
							{renderCreatedHeader()}
							{renderRenewalHeader()}
							{renderLastMeeting()}
							{renderNextMeeting()}
						</>
					) : (
						<>
							<div className={css(styleSheet.accountListHeaderStandard)}>Industry</div>
							<div className={css(styleSheet.accountListHeaderStandard)}>Success</div>
							{renderCreatedHeader()}
							{renderRenewalHeader()}
							{renderLastMeeting()}
							{renderNextMeeting()}
						</>
					)}
				</div>
				<div>
					{adminAccountFilterQuery.data?.pages.map(page => {
						return page.values.map(account => {
							return (
								<AccountRow
									accountVM={new AdminApi.AccountAdminViewModel(userSession, account)}
									isOwnedByMode={isOwnedByMode}
									key={account.id}
								/>
							);
						});
					})}
					{adminAccountFilterQuery.hasNextPage && !adminAccountFilterQuery.isPending ? (
						<Waypoint bottomOffset='-200px' onEnter={() => adminAccountFilterQuery.fetchNextPage()} />
					) : null}
					{adminAccountFilterQuery.isFetchingNextPage ? <LoadingSpinner type='large' /> : null}
				</div>
			</div>
			<UserSelectModal
				modalProps={{
					isOpen: showingOwnedByModal,
					onRequestClose: onOwnedByModalRequestClose,
				}}
				title='Accounts owned by'
			/>
			<UserSelectModal
				modalProps={{
					isOpen: showingSoldByModal,
					onRequestClose: onSoldByModalRequestClose,
				}}
				title='Accounts sold by'
			/>
			<RadioButtonOptionsDialog
				modalProps={{
					isOpen: !!showingIndustryModal,
					onRequestClose: onRemoveFromTagDialogRequestClose,
				}}
				options={industryRadios}
				title='Accounts by industry'
			/>
		</div>
	);
}
